import { HttpClient } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { map, switchMap, take, tap, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { updateUser } from '../redux/actions/user.actions';
import { AppState } from '../redux/app.state';
import { Bank, OrderListDefaults, Organization, Role, User, UserRef, Warehouse } from 'src/typings';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  user: Observable<User> = this.store.pipe(map((data) => data.user.info));

  constructor(private store: Store<AppState>, private http: HttpClient) {}

  getUser(id: number) {
    return this.http.get<User>(`${environment.BACKEND}/user/info/${id}`);
  }

  updateUser() {
    this.user
      .pipe(
        filter((user): user is User => Boolean(user?.id)),
        take(1),
        switchMap((user) => this.getUser(user.id)),
        tap((user) => {
          this.store.dispatch(updateUser({ payload: user }));
        })
      )
      .subscribe();
  }

  getAuthUser(): Observable<User> {
    return this.user;
  }

  setOrderListDefaults(defaults: OrderListDefaults | null, ownerId: number): Observable<User> {
    return this.user.pipe(take(1), switchMap(user => {
      if (user?.id !== ownerId) return throwError(() => new Error('Пользователь изменился. Откройте настройки заново.'));
      return this.http.put<User>(`${environment.BACKEND}/user/order-list-defaults`, { defaults });
    }), tap(user => {
      this.user.pipe(take(1)).subscribe(current => {
        if (current?.id === ownerId) this.store.dispatch(updateUser({ payload: user }));
      });
    }));
  }

  setPartsSelectionAiEnabled(enabled: boolean): Observable<User> {
    return this.http
      .put<User>(`${environment.BACKEND}/user/update`, {
        partsSelectionAiEnabled: enabled
      })
      .pipe(tap((user) => this.store.dispatch(updateUser({ payload: user }))));
  }

  getBanks() {
    return this.http.get<Bank[]>(`${environment.BACKEND}/user/banks`);
  }

  getListUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${environment.BACKEND}/user/list`);
  }

  getRefListUsers(onlyActive = true): Observable<UserRef[]> {
    return this.http.get<UserRef[]>(`${environment.BACKEND}/user/ref-list`, {
      params: onlyActive ? undefined : new HttpParams().set('includeInactive', 'true')
    });
  }

  createUser(
    name: string,
    phone: string,
    password: string,
    roles: Role[],
    banks: Bank[],
    defaultWarehouse: Warehouse,
    defaultOrg: Organization
  ): Observable<User> {
    const rolesWrapper = roles.map((role) => role.id);
    const banksWrapper = banks.map((bank) => bank.id);
    return this.http.post<User>(`${environment.BACKEND}/user/create`, {
      name,
      phone,
      password,
      roleId: rolesWrapper,
      bankId: banksWrapper,
      defaultWarehouseId: defaultWarehouse.id,
      defaultOrgId: defaultOrg.id
    });
  }

  deleteUser(id: number): Observable<User> {
    return this.http.delete<User>(`${environment.BACKEND}/user/delete/${id}`);
  }
}
